import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  Stack,
  useColorModeValue,
  Image,
  HStack,
} from "@chakra-ui/react";
import {
  FaRocket,
  FaBrain,
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  FaCog,
  FaUserGraduate,
  FaCheckCircle,
  FaGlobe,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";
import { seoData } from "../utils/seoData";
import backgroundImage from "../assets/img/background.jpeg";

const Feature = ({ title, text, icon }) => {
  return (
    <Stack
      bg={useColorModeValue("white", "gray.800")}
      p={10}
      rounded="3xl"
      shadow="xl"
      transition="all 0.4s"
      _hover={{ transform: "translateY(-8px)", shadow: "2xl" }}
      border="1px"
      borderColor={useColorModeValue("gray.100", "gray.700")}
      h="full"
    >
      <Flex
        w={20}
        h={20}
        align="center"
        justify="center"
        color="white"
        rounded="2xl"
        bgGradient="linear(to-br, #FF6A5F, #A63FF5)"
        mb={6}
      >
        {icon}
      </Flex>
      <Heading size="lg" mb={4} fontWeight="bold">
        {title}
      </Heading>
      <Text
        color="gray.600"
        fontSize="md"
        lineHeight="1.8"
        wordBreak="keep-all"
        whiteSpace="pre-line"
        style={{
          wordBreak: "keep-all",
          overflowWrap: "break-word",
        }}
      >
        {text}
      </Text>
    </Stack>
  );
};

const StatCard = ({ icon, value, label, description }) => {
  return (
    <VStack spacing={4} py={6}>
      <Flex w={12} h={12} align="center" justify="center" p={3}>
        {icon}
      </Flex>
      <Heading
        size="3xl"
        fontWeight="bold"
        bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
        bgClip="text"
      >
        {value}
      </Heading>
      <Heading size="md" fontWeight="semibold">
        {label}
      </Heading>
      <Text fontSize="md" color="gray.600" textAlign="center" opacity={0.7}>
        {description}
      </Text>
    </VStack>
  );
};

const WhyItem = ({ title, description }) => {
  return (
    <HStack align="flex-start" spacing={4} mb={6}>
      <Flex
        minW={12}
        h={12}
        align="center"
        justify="center"
        rounded="full"
        bg="rgba(166, 63, 245, 0.1)"
      >
        <Icon as={FaCheckCircle} w={6} h={6} color="#A63FF5" />
      </Flex>
      <VStack align="flex-start" spacing={2}>
        <Heading size="md" fontWeight="bold">
          {title}
        </Heading>
        <Text
          color="gray.600"
          fontSize="md"
          lineHeight="1.7"
          wordBreak="keep-all"
          style={{
            wordBreak: "keep-all",
            overflowWrap: "break-word",
          }}
        >
          {description}
        </Text>
      </VStack>
    </HStack>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const seo = seoData.home[language] || seoData.home.en;

  // Color mode values at component level
  const serviceBgColor = useColorModeValue("white", "gray.800");

  const translations = {
    en: {
      heroTitle: "Maximize Learning Potential with AI",
      heroSubtitle:
        "Empowering education and learning through cutting-edge AI technology. Create, manage, and enhance educational content with our intelligent platform.",
      heroPrimary: "Explore Products",
      heroSecondary: "Get in Touch",
      statsHeading: "Trusted by Educators Worldwide",
      stats: [
        {
          value: "10K+",
          label: "Active Users",
          description: "Educators and students using our platform daily",
        },
        {
          value: "5+",
          label: "Institutions",
          description: "Leading educational organizations trust us",
        },
        {
          value: "1M+",
          label: "Problems Solved",
          description: "Educational content pieces created",
        },
        {
          value: "3+",
          label: "Countries",
          description: "Global reach across multiple regions",
        },
      ],
      aiModelsHeading: "Powered by Leading AI Models",
      aiModelsSubheading:
        "We integrate the world's most advanced AI technologies to deliver exceptional results",
      servicesHeading: "Comprehensive Solutions",
      servicesSubheading:
        "We provide comprehensive tools and services to transform your educational experience",
      servicesDescription:
        "Our AI-powered platform offers a complete suite of tools designed for modern education. From problem bank management to advanced analytics, we provide everything you need to deliver exceptional learning experiences.",
      servicesLearnMore: "Learn more",
      services: [
        {
          title: "Circuit Training",
          description: "Structured practice programs with rotating exercises",
        },
        {
          title: "Problem Bank",
          description: "Organize and manage question banks efficiently",
        },
        {
          title: "Analytics",
          description: "Track performance with detailed insights",
        },
        {
          title: "Student Management",
          description: "Manage students and assignments seamlessly",
        },
      ],
      whyHeading: "Why Choose AIDU?",
      whySubheading:
        "Innovative solutions that make education more effective and accessible for everyone.",
      whyItems: [
        {
          title: "Quality",
          description:
            "Premium educational content and tools backed by AI technology and expert curation.",
        },
        {
          title: "Efficiency",
          description:
            "Save time with automated content generation and intelligent workflow management.",
        },
        {
          title: "Reliability",
          description:
            "99.9% uptime and enterprise-grade security ensuring your data is always safe.",
        },
      ],
      featuresHeading: "Powerful Features",
      featuresDescription:
        "Everything you need to create, manage, and deliver exceptional educational content",
      features: [
        {
          title: "AI-Powered",
          text:
            "Leverage advanced artificial intelligence to create and customize educational content automatically.",
          icon: FaBrain,
        },
        {
          title: "Analytics & Insights",
          text:
            "Track progress and performance with detailed analytics and actionable insights.",
          icon: FaChartLine,
        },
        {
          title: "Collaborative",
          text:
            "Enable seamless collaboration between teachers, students, and administrators.",
          icon: FaUsers,
        },
        {
          title: "Secure & Reliable",
          text:
            "Enterprise-grade security ensuring your data is always protected and accessible.",
          icon: FaShieldAlt,
        },
        {
          title: "Easy Integration",
          text: "Seamlessly integrate with your existing tools and workflows.",
          icon: FaCog,
        },
        {
          title: "Fast & Scalable",
          text:
            "Built for performance and scale, handling thousands of users effortlessly.",
          icon: FaRocket,
        },
      ],
      ctaTitle: "Ready to Get Started?",
      ctaDescription:
        "Join thousands of educators and institutions using our platform to revolutionize learning.",
      ctaButton: "Contact Sales",
    },
    ko: {
      heroTitle: "AI로 학습 잠재력을 \n극대화하세요",
      heroSubtitle:
        "최첨단 AI 기술로 교육과 학습을 혁신합니다.\n지능형 플랫폼으로 교육 콘텐츠를 만들고, 관리하고, 향상하세요.",
      heroPrimary: "제품 살펴보기",
      heroSecondary: "문의하기",
      statsHeading: "전 세계 교육자들의 신뢰",
      stats: [
        {
          value: "10K+",
          label: "활성 사용자",
          description: "매일 플랫폼을 사용하는 교육자와 학생들",
        },
        {
          value: "5+",
          label: "교육 기관",
          description: "선도적인 교육 기관들이 신뢰합니다",
        },
        {
          value: "1M+",
          label: "해결된 문제",
          description: "생성된 교육 콘텐츠 수",
        },
        {
          value: "3+",
          label: "서비스 국가",
          description: "여러 지역에 걸친 글로벌 도달 범위",
        },
      ],
      aiModelsHeading: "세계 최고의 AI 모델 활용",
      aiModelsSubheading: "최첨단 AI 기술을 통합하여 탁월한 결과를 제공합니다",
      servicesHeading: "종합 솔루션",
      servicesSubheading:
        "교육 경험을 혁신하는 포괄적인 도구와 서비스를 제공합니다",
      servicesDescription:
        "AI 기반 플랫폼은 현대 교육을 위해 설계된 완전한 도구 모음을 제공합니다.\n문제은행 관리부터 고급 분석까지, 탁월한 학습 경험을 제공하는 데 \n필요한 모든 것을 갖추고 있습니다.",
      servicesLearnMore: "자세히 보기",
      services: [
        {
          title: "서킷 트레이닝",
          description: "회전식 연습으로 구성된 체계적인 학습 프로그램",
        },
        {
          title: "문제은행",
          description: "문제은행을 효율적으로 관리하고 정리",
        },
        {
          title: "분석 도구",
          description: "상세한 인사이트로 성과 추적",
        },
        {
          title: "학생 관리",
          description: "학생과 과제를 원활하게 관리",
        },
      ],
      whyHeading: "왜 AIDU인가요?",
      whySubheading:
        "모두를 위해 교육을 더 효과적이고 접근 가능하게 만드는 혁신적인 솔루션.",
      whyItems: [
        {
          title: "품질",
          description:
            "AI 기술과 전문가 큐레이션으로 뒷받침되는 프리미엄 교육 콘텐츠와 도구.",
        },
        {
          title: "효율성",
          description:
            "자동화된 콘텐츠 생성과 지능형 워크플로 관리로 시간을 절약하세요.",
        },
        {
          title: "신뢰성",
          description:
            "99.9% 가동률과 기업급 보안으로 데이터를 항상 안전하게 보호합니다.",
        },
      ],
      featuresHeading: "강력한 기능",
      featuresDescription:
        "탁월한 교육 콘텐츠를 만들고 관리하고 전달하는 데 필요한 모든 것",
      features: [
        {
          title: "AI 기반",
          text:
            "첨단 인공지능을 활용해 교육 콘텐츠를 \n자동으로 생성하고 맞춤화합니다.",
          icon: FaBrain,
        },
        {
          title: "분석과 인사이트",
          text:
            "상세한 분석과 실행 가능한 인사이트로 학습 \n진행 상황과 성과를 추적하세요.",
          icon: FaChartLine,
        },
        {
          title: "협업 지원",
          text:
            "교사, 학생, 관리자가 매끄럽게 \n협업할 수 있는 환경을 제공합니다.",
          icon: FaUsers,
        },
        {
          title: "안전하고 안정적",
          text: "기업 수준의 보안으로 데이터를 \n언제나 안전하게 보호합니다.",
          icon: FaShieldAlt,
        },
        {
          title: "손쉬운 통합",
          text: "현재 사용 중인 도구와 워크플로와 \n자연스럽게 연동됩니다.",
          icon: FaCog,
        },
        {
          title: "빠르고 확장 가능",
          text:
            "수천 명의 사용자를 안정적으로 지원하는 \n성능과 확장성을 갖추고 있습니다.",
          icon: FaRocket,
        },
      ],
      ctaTitle: "지금 시작해 볼까요?",
      ctaDescription:
        "전 세계 수많은 교육 기관이 AIDU와 함께 학습을 혁신하고 있습니다.",
      ctaButton: "영업팀에 문의하기",
    },
  };

  const t = translations[language] || translations.en;

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/"
        lang={language}
        ogImage="/screenshots/circuit-training.png"
      />
      <Box>
        {/* Hero Section */}
        <Box
          bgGradient="linear(to-br, #FF6A5F, #A63FF5, #C56CFF)"
          color="white"
          py={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background Image Layer */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgImage={`url(${backgroundImage})`}
            bgSize="cover"
            bgPosition="center"
            opacity={0.2}
            zIndex={0}
          />

          <Container maxW="container.xl" position="relative" zIndex={1}>
            <Flex
              direction={{ base: "column", lg: "row" }}
              align="center"
              justify="space-between"
              gap={12}
            >
              <VStack
                align={{ base: "center", lg: "flex-start" }}
                spacing={8}
                flex={1}
              >
                <Heading
                  fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                  fontWeight="bold"
                  textAlign={{ base: "center", lg: "left" }}
                  lineHeight="1.2"
                  whiteSpace="pre-line"
                >
                  {t.heroTitle}
                </Heading>
                <Text
                  fontSize={{ base: "lg", md: "xl" }}
                  textAlign={{ base: "center", lg: "left" }}
                  maxW="650px"
                  lineHeight="tall"
                  whiteSpace="pre-line"
                >
                  {t.heroSubtitle}
                </Text>
                <Stack
                  direction={{ base: "column", sm: "row" }}
                  spacing={4}
                  pt={4}
                >
                  <Button
                    size="lg"
                    h="60px"
                    px={10}
                    fontSize="lg"
                    variant="solid"
                    bg="white"
                    color="#A63FF5"
                    rounded="full"
                    _hover={{
                      bg: "gray.100",
                      transform: "translateY(-2px)",
                      shadow: "2xl",
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/products")}
                  >
                    {t.heroPrimary}
                  </Button>
                  <Button
                    size="lg"
                    h="60px"
                    px={10}
                    fontSize="lg"
                    variant="outline"
                    borderColor="white"
                    borderWidth="2px"
                    color="white"
                    rounded="full"
                    _hover={{
                      bg: "whiteAlpha.200",
                      transform: "translateY(-2px)",
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    {t.heroSecondary}
                  </Button>
                </Stack>
              </VStack>
              <Box flex={1} display={{ base: "none", lg: "block" }}>
                <Image
                  src="/screenshots/circuit-training.png"
                  alt="Circuit Training Program"
                  rounded="3xl"
                  shadow="dark-lg"
                  w="full"
                  h="auto"
                  objectFit="contain"
                />
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box py={20}>
          <Container maxW="container.xl">
            <VStack spacing={12}>
              <Heading size="2xl" textAlign="center" fontWeight="bold">
                {t.statsHeading}
              </Heading>
              <SimpleGrid
                columns={{ base: 1, sm: 2, lg: 4 }}
                spacing={10}
                w="full"
              >
                {t.stats.map((stat, index) => (
                  <StatCard
                    key={index}
                    icon={
                      <Icon
                        as={
                          index === 0
                            ? FaUsers
                            : index === 1
                            ? FaUserGraduate
                            : index === 2
                            ? FaChartLine
                            : FaGlobe
                        }
                        w={8}
                        h={8}
                        color="#A63FF5"
                      />
                    }
                    value={stat.value}
                    label={stat.label}
                    description={stat.description}
                  />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Services Section */}
        <Box py={24} bg={useColorModeValue("gray.50", "gray.900")}>
          <Container maxW="container.xl">
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing={16}
              alignItems="center"
            >
              {/* Left Column - Text */}
              <VStack align={{ base: "center", lg: "flex-start" }} spacing={6}>
                <Heading
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="bold"
                  textAlign={{ base: "center", lg: "left" }}
                  lineHeight="shorter"
                >
                  {t.servicesHeading}
                </Heading>
                <Text
                  fontSize="lg"
                  color="gray.600"
                  textAlign={{ base: "center", lg: "left" }}
                  lineHeight="1.8"
                  whiteSpace="pre-line"
                >
                  {t.servicesDescription}
                </Text>
                <Button
                  size="lg"
                  variant="link"
                  color="#A63FF5"
                  fontSize="xl"
                  rightIcon={<Icon as={FaRocket} />}
                  _hover={{ transform: "translateX(5px)" }}
                  transition="all 0.3s"
                  onClick={() => navigate("/products")}
                >
                  {t.servicesLearnMore}
                </Button>
              </VStack>

              {/* Right Column - Service Cards */}
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={8}>
                {t.services.map((service, index) => (
                  <Box
                    key={index}
                    bg={serviceBgColor}
                    p={8}
                    rounded="3xl"
                    shadow="xl"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-8px)", shadow: "2xl" }}
                  >
                    <Icon
                      as={
                        index === 0
                          ? FaBrain
                          : index === 1
                          ? FaChartLine
                          : index === 2
                          ? FaChartLine
                          : FaUsers
                      }
                      w={12}
                      h={12}
                      color="#A63FF5"
                      mb={4}
                    />
                    <Heading size="md" mb={3}>
                      {service.title}
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                      {service.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Why Section */}
        <Box py={24}>
          <Container maxW="container.xl">
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing={16}
              alignItems="center"
            >
              {/* Left Column - Image */}
              <Box display={{ base: "none", lg: "block" }}>
                <Image
                  src="/screenshots/student-report.png"
                  alt="Analytics Dashboard"
                  rounded="3xl"
                  shadow="2xl"
                  w="full"
                  h="auto"
                />
              </Box>

              {/* Right Column - Why Items */}
              <VStack align="flex-start" spacing={8}>
                <VStack
                  align={{ base: "center", lg: "flex-start" }}
                  spacing={4}
                >
                  <Heading
                    fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                    fontWeight="bold"
                    textAlign={{ base: "center", lg: "left" }}
                  >
                    {t.whyHeading}
                  </Heading>
                  <Text
                    fontSize="lg"
                    color="gray.600"
                    textAlign={{ base: "center", lg: "left" }}
                    lineHeight="1.8"
                  >
                    {t.whySubheading}
                  </Text>
                </VStack>

                <VStack align="flex-start" spacing={6} w="full" pt={4}>
                  {t.whyItems.map((item, index) => (
                    <WhyItem
                      key={index}
                      title={item.title}
                      description={item.description}
                    />
                  ))}
                </VStack>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box py={24} bg={useColorModeValue("gray.50", "gray.900")}>
          <Container maxW="container.xl">
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="bold"
                >
                  {t.featuresHeading}
                </Heading>
                <Text fontSize="lg" color="gray.600" maxW="700px">
                  {t.featuresDescription}
                </Text>
              </VStack>

              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={10}
                w="full"
              >
                {t.features.map((feature, index) => (
                  <Feature
                    key={index}
                    icon={<Icon as={feature.icon} w={10} h={10} />}
                    title={feature.title}
                    text={feature.text}
                  />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box py={24}>
          <Container maxW="container.xl">
            <Flex
              direction="column"
              align="center"
              justify="center"
              bgGradient="linear(135deg, rgba(255, 106, 95, 0.1), rgba(166, 63, 245, 0.1))"
              rounded="3xl"
              p={{ base: 12, md: 20 }}
              textAlign="center"
              border="1px"
              borderColor={useColorModeValue("gray.200", "gray.700")}
            >
              <Heading
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="bold"
                mb={6}
              >
                {t.ctaTitle}
              </Heading>
              <Text
                fontSize="xl"
                color="gray.600"
                mb={10}
                maxW="700px"
                lineHeight="tall"
              >
                {t.ctaDescription}
              </Text>
              <Button
                size="lg"
                h="60px"
                px={12}
                fontSize="lg"
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                rounded="full"
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                  transform: "translateY(-4px)",
                  shadow: "2xl",
                }}
                transition="all 0.3s"
                onClick={() => navigate("/contact")}
              >
                {t.ctaButton}
              </Button>
            </Flex>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Home;
